import {
  FloatingFocusManager,
  FloatingNode,
  useFloating,
  useFloatingNodeId,
  useInteractions,
} from '@floating-ui/react';
import clsx from 'clsx';
import { memo, useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { AppNavPresentationProvider } from '../../../context/AppNavPresentation/AppNavPresentation';
import {
  FloatingPriorityRoot,
  useEscapePriorityDismiss,
} from '../../../utils/floatingDismissPriority';
import { useRevealPresence } from '../../../utils/useRevealPresence';
import './NavDrawer.css';

interface NavDrawerProps {
  children: ReactNode;
  isOpen: boolean;
  /** Element the drawer portals into. Defaults to the document body. */
  root?: HTMLElement;
  onClose?: () => void;
}

function NavDrawerContent({ children, isOpen, root, onClose }: NavDrawerProps) {
  const nodeId = useFloatingNodeId();
  const backdropRef = useRef<HTMLDivElement>(null);
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const { isLeaving, isRendered } = useRevealPresence(
    isOpen,
    backdropRef,
    '--_reveal-exit',
  );

  const { context, refs } = useFloating({
    nodeId,
    open: isOpen,
    onOpenChange: (open) => {
      if (!open) {
        onClose?.();
      }
    },
  });
  const dismiss = useEscapePriorityDismiss(context, { outsidePress: false });
  const { getFloatingProps } = useInteractions([dismiss]);

  useEffect(() => {
    if (surfaceRef.current) {
      surfaceRef.current.inert = !isOpen;
    }
  }, [isOpen]);

  return createPortal(
    <FloatingNode id={nodeId}>
      <div className="eds-app-layout-nav-drawer">
        {isRendered && (
          <div
            aria-hidden="true"
            className={clsx(
              'eds-app-layout-nav-drawer--backdrop',
              isLeaving && 'eds-app-layout-nav-drawer--backdrop--leaving',
            )}
            ref={backdropRef}
            onClick={onClose}
          />
        )}
        <FloatingFocusManager context={context} disabled={!isOpen}>
          <div
            aria-hidden={!isOpen}
            className={clsx(
              'eds-app-layout-nav-drawer--surface',
              isOpen && 'eds-app-layout-nav-drawer--surface--open',
            )}
            ref={(element) => {
              surfaceRef.current = element;
              refs.setFloating(element);
            }}
            {...getFloatingProps()}
          >
            <AppNavPresentationProvider onClose={onClose} presentation="drawer">
              {children}
            </AppNavPresentationProvider>
          </div>
        </FloatingFocusManager>
      </div>
    </FloatingNode>,
    root ?? document.body,
  );
}

/**
 * Hosts the side navigation as a slide-in drawer on narrow viewports. The
 * drawer stays mounted so its state survives and its slide can animate; while
 * closed it is inert, so nothing in it can take focus.
 */
export const NavDrawer = memo(function NavDrawer(props: NavDrawerProps) {
  return (
    <FloatingPriorityRoot>
      <NavDrawerContent {...props} />
    </FloatingPriorityRoot>
  );
});

NavDrawer.displayName = 'AppLayoutNavDrawer';
